import { type ReactNode, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import DataLayer from '~/services/DataLayer';

import ActionForm from '../ActionForm';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';
import RedirectToHiddenInput from '../Forms/RedirectToHiddenInput';
import StatusInputs from '../Forms/StatusInputs';

type RenderProps = {
  isLoading: boolean;
};

type Props = {
  children: (props: RenderProps) => ReactNode;
  onSuccess?: () => void;
};

const UpdatePasswordAction = ({ children, onSuccess }: Props) => {
  const fetcher = useComponentFetcher();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSuccess = (data: any) => {
    setIsLoading(false);

    if (data?.dataLayerEvent) {
      DataLayer.pushEvent(data.dataLayerEvent);
    }

    toast.success(data?.toast?.message);
    formRef.current?.reset();

    onSuccess?.();
  };

  return (
    <ActionForm
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action="/request_update_password"
      onSubmit={(e) => {
        setIsLoading(true);
        const formData = new FormData(e.currentTarget);

        fetcher.submit(formData, {
          method: 'post',
          action: '/request_update_password',
        });
      }}
      onSuccess={(data) => {
        handleSuccess(data);
      }}
      onError={(data) => {
        setIsLoading(false);
        toast.error(data?.toast?.message || 'Something went wrong, please try again');
      }}
    >
      <RedirectToHiddenInput redirectToHome />
      <StatusInputs
        name="reset_password"
        errorMessage={`${t('errors.default')} ${t('errors.try_later')}`}
        successMessage={t('toast.success')}
      />
      {children({ isLoading })}
    </ActionForm>
  );
};

export default UpdatePasswordAction;
